import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Pencil, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { BonkerLogo } from "@/components/BonkerLogo";
import { Stepper } from "@/components/configurator/Stepper";
import { OptionGrid, type Option } from "@/components/configurator/OptionGrid";
import {
  ConfigState, PriceResult, STEP_KEYS, STEP_LABELS, StepKey, CATEGORIES,
  MAX_DESCRIPTION, isOtherOption, isGraficheDecorative,
  emptyState, saveDraft, loadDraft, clearDraft, visibleSteps,
  nextIndex, prevIndex, contactValid, estimatePrice, formatPrice,
} from "@/lib/configurator";

interface Catalog { models: any[]; designs: any[]; areas: Option[]; types: any[]; details: any[]; }

export default function ConfiguratorPage() {
  const [state, setState] = useState<ConfigState>(() => loadDraft() ?? emptyState());
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [price, setPrice] = useState<PriceResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneId, setDoneId] = useState<string | null>(null);
  const [logo, setLogo] = useState<string | null>(null);

  const navigate = useNavigate();
  const currentKey = STEP_KEYS[state.stepIndex] as StepKey;

  useEffect(() => {
    api.get("/settings").then(({ data }) => setLogo(data.logo_url)).catch(() => {});
    Promise.all([
      api.get("/models"), api.get("/themes"), api.get("/areas"),
      api.get("/types"), api.get("/details"),
    ]).then(([m, d, a, ty, de]) => {
      setCatalog({ models: m.data, designs: d.data, areas: a.data, types: ty.data, details: de.data });
    }).catch(() => setCatalog({ models: [], designs: [], areas: [], types: [], details: [] }));
  }, []);

  useEffect(() => { saveDraft(state); }, [state]);

  useEffect(() => {
    if (!state.category) { setPrice(null); return; }
    if (state.category !== "baby" && !state.is_colors_only && !state.is_decorative && !state.type_id) { setPrice(null); return; }
    let cancelled = false;
    estimatePrice(state).then((p) => { if (!cancelled) setPrice(p); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.category, state.area_id, state.type_id, state.detail_id, state.is_colors_only, state.is_decorative]);

  const patch = useCallback((p: Partial<ConfigState>) => setState((s) => ({ ...s, ...p })), []);
  const goNext = () => patch({ stepIndex: nextIndex(state.stepIndex, state.skip_details) });
  const goPrev = () => patch({ stepIndex: prevIndex(state.stepIndex, state.skip_details) });
  const jumpTo = (index: number) => patch({ stepIndex: index });

  const optName = (list: any[], id: string | null) => (list ?? []).find((x) => x.id === id)?.name ?? "";
  const computeSkip = (category: string | null, colorsOnly: boolean, designName: string) =>
    category === "baby" || colorsOnly || isGraficheDecorative(designName);
  const norm = (s: string) => (s ?? "").trim().toLowerCase();
  const areaName = optName(catalog?.areas ?? [], state.area_id);
  const typeName = optName(catalog?.types ?? [], state.type_id);
  const filteredTypes = useMemo(() => {
    const all = catalog?.types ?? [];
    return norm(areaName) === "lato esterno"
      ? all.filter((t: any) => norm(t.name) !== "soggetto con sfondo lato esterno + sfondo lato interno")
      : all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, areaName]);
  const filteredDetails = useMemo(() => {
    const all = catalog?.details ?? [];
    const tn = norm(typeName);
    if (tn === "soggetto") return all.filter((d: any) => norm(d.name) !== "full art");
    if (tn === "composizione") return all.filter((d: any) => norm(d.name) !== "design semplice");
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, typeName]);

  const idx = (k: StepKey) => STEP_KEYS.indexOf(k);

  const completed = (key: StepKey): boolean => {
    switch (key) {
      case "category": return !!state.category;
      case "model": return !!state.model_id && (!isOtherOption(optName(catalog?.models ?? [], state.model_id)) || !!state.model_other.trim());
      case "design": return !!state.design_id && (!isOtherOption(optName(catalog?.designs ?? [], state.design_id)) || !!state.design_other.trim());
      case "area": return !!state.area_id;
      case "realization": return !!state.type_id;
      case "detail": return !!state.detail_id;
      case "description": return state.stepIndex > idx("description");
      case "summary": return state.stepIndex > idx("summary");
      case "contact": return contactValid(state.contact);
      default: return false;
    }
  };

  // --- selection handlers ---
  const selectCategory = (item: Option) =>
    setState((s) => {
      const skip = computeSkip(item.id, s.is_colors_only, optName(catalog?.designs ?? [], s.design_id));
      return {
        ...s, category: item.id, skip_details: skip,
        area_id: skip ? null : s.area_id, type_id: skip ? null : s.type_id, detail_id: skip ? null : s.detail_id,
        stepIndex: nextIndex(idx("category"), skip),
      };
    });

  const selectModel = (item: Option) => {
    if (isOtherOption(item.name)) { patch({ model_id: item.id }); return; } // wait for Specifica
    setState((s) => ({ ...s, model_id: item.id, model_other: "", stepIndex: nextIndex(idx("model"), s.skip_details) }));
  };

  const selectDesign = (item: any) => {
    const colors = !!item.is_colors_only || item.name === "Solo colori";
    const decorative = isGraficheDecorative(item.name);
    if (isOtherOption(item.name)) {
      setState((s) => ({ ...s, design_id: item.id, is_colors_only: false, is_decorative: false, skip_details: computeSkip(s.category, false, item.name) }));
      return;
    }
    setState((s) => {
      const skip = computeSkip(s.category, colors, item.name);
      return {
        ...s, design_id: item.id, design_other: "", is_colors_only: colors, is_decorative: decorative, skip_details: skip,
        area_id: skip ? null : s.area_id, type_id: skip ? null : s.type_id, detail_id: skip ? null : s.detail_id,
        stepIndex: nextIndex(idx("design"), skip),
      };
    });
  };

  const selectArea = (item: Option) =>
    setState((s) => ({ ...s, area_id: item.id, type_id: null, detail_id: null, stepIndex: nextIndex(idx("area"), s.skip_details) }));
  const selectRealization = (item: Option) =>
    setState((s) => ({ ...s, type_id: item.id, detail_id: null, stepIndex: nextIndex(idx("realization"), s.skip_details) }));
  const selectDetail = (item: Option) =>
    setState((s) => ({ ...s, detail_id: item.id, stepIndex: nextIndex(idx("detail"), s.skip_details) }));

  const submit = async () => {
    if (!contactValid(state.contact)) { toast.error("Completa i contatti: nome, email valida e privacy."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/requests", {
        category: state.category,
        theme_id: state.design_id, model_id: state.model_id,
        model_other: state.model_other || null, design_other: state.design_other || null,
        area_id: state.area_id, type_id: state.type_id, detail_id: state.detail_id,
        is_colors_only: state.is_colors_only,
        description: state.description,
        estimated_price_min: price?.price_min ?? null,
        estimated_price_max: price?.price_max ?? null,
        contact: state.contact,
      });
      clearDraft(); setDoneId(data.id);
    } catch (err: any) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSubmitting(false); }
  };

  const resetAll = () => { clearDraft(); setState(emptyState()); setPrice(null); setDoneId(null); };

  if (!catalog)
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  if (doneId)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 bonker-noise relative" data-testid="configurator-success">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-md text-center relative z-10">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-secondary text-black mb-6"><PartyPopper className="h-8 w-8" /></div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-3">Richiesta inviata!</h1>
          <p className="text-muted-foreground mb-2">Grazie! Ti ricontatteremo al più presto.</p>
          <p className="text-sm text-muted-foreground mb-1">Codice richiesta: <span className="text-secondary font-mono" data-testid="success-request-id">{doneId}</span></p>
          {price?.found && <p className="text-sm mb-8">Stima: <span className="text-primary font-bold">{formatPrice(price)}</span></p>}
          <button onClick={resetAll} data-testid="configurator-restart" className="bg-primary text-primary-foreground font-bold uppercase tracking-[0.15em] text-sm px-8 py-4 hover:-translate-y-[2px] transition-transform">
            Nuova configurazione
          </button>
        </motion.div>
      </div>
    );

  const modelIsOther = !!state.model_id && isOtherOption(optName(catalog.models, state.model_id));
  const designIsOther = !!state.design_id && isOtherOption(optName(catalog.designs, state.design_id));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 md:px-10 sticky top-0 z-40 bg-background">
        <button data-testid="configurator-logo-home" onClick={() => { resetAll(); navigate("/"); }} aria-label="Torna alla home"><BonkerLogo src={logo ? mediaUrl(logo) : null} className="h-9" /></button>
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground hidden sm:block">Configuratore</span>
      </header>

      <div className="grid md:grid-cols-[18rem_1fr] min-h-[calc(100vh-4rem)]">
        <aside className="hidden md:block border-r border-white/10 py-8 sticky top-16 self-start">
          <p className="px-4 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60 mb-3">Passi</p>
          <Stepper currentIndex={state.stepIndex} soloColori={state.skip_details} onJump={jumpTo} completed={completed} />
        </aside>

        <main className="p-6 md:p-10 pb-32">
          <AnimatePresence mode="wait">
            <motion.div key={currentKey} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} data-testid={`step-${currentKey}`}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-2">
                Step {visibleSteps(state.skip_details).indexOf(currentKey) + 1} · {STEP_LABELS[currentKey]}
              </p>

              {currentKey === "category" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Categoria della scarpa</h1>
                  <OptionGrid items={CATEGORIES} selectedId={state.category} onSelect={selectCategory} testidPrefix="category" />
                </>
              )}

              {currentKey === "model" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Modello di sneaker</h1>
                  <OptionGrid items={catalog.models} selectedId={state.model_id} onSelect={selectModel} testidPrefix="model" />
                  {modelIsOther ? (
                    <OtherField label="Specifica il modello" testid="model-other" value={state.model_other}
                      onChange={(v) => patch({ model_other: v })} onNext={goNext} onPrev={goPrev} />
                  ) : (
                    <BackOnly onPrev={goPrev} />
                  )}
                </>
              )}

              {currentKey === "design" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Tipo di design</h1>
                  <OptionGrid items={catalog.designs} selectedId={state.design_id} onSelect={selectDesign} testidPrefix="design" />
                  {designIsOther ? (
                    <OtherField label="Specifica il tema" testid="design-other" value={state.design_other}
                      onChange={(v) => patch({ design_other: v })} onNext={goNext} onPrev={goPrev} />
                  ) : (
                    <BackOnly onPrev={goPrev} />
                  )}
                </>
              )}

              {currentKey === "area" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Area da personalizzare</h1>
                  <OptionGrid items={catalog.areas} selectedId={state.area_id} onSelect={selectArea} testidPrefix="area" />
                  <BackOnly onPrev={goPrev} />
                </>
              )}
              {currentKey === "realization" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Tipo di realizzazione</h1>
                  <OptionGrid items={filteredTypes} selectedId={state.type_id} onSelect={selectRealization} testidPrefix="realization" />
                  <BackOnly onPrev={goPrev} />
                </>
              )}
              {currentKey === "detail" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Livello di dettaglio</h1>
                  <OptionGrid items={filteredDetails} selectedId={state.detail_id} onSelect={selectDetail} testidPrefix="detail" />
                  <BackOnly onPrev={goPrev} />
                </>
              )}

              {currentKey === "description" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Descrizione del progetto</h1>
                  <p className="text-muted-foreground mb-8">Campo obbligatorio.</p>
                  <textarea data-testid="description-input" value={state.description}
                    onChange={(e) => patch({ description: e.target.value.slice(0, MAX_DESCRIPTION) })}
                    rows={8} maxLength={MAX_DESCRIPTION}
                    placeholder="Esempio: Luffy Gear 5 in una scarpa e Zoro nell'altra, con il cielo grigio/nero come sfondo per entrambi."
                    className="w-full max-w-2xl border border-input bg-black/50 p-4 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50" />
                  <p className="text-xs text-muted-foreground mt-2" data-testid="description-counter">{state.description.length}/{MAX_DESCRIPTION}</p>
                  <StepNav onPrev={goPrev} onNext={goNext} nextDisabled={!state.description.trim()} />
                </>
              )}

              {currentKey === "summary" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Riepilogo</h1>
                  <p className="text-muted-foreground mb-8">Controlla e modifica prima di proseguire.</p>
                  <div className="max-w-2xl border border-white/10 divide-y divide-white/10" data-testid="summary-list">
                    <SummaryRow label="Categoria" value={CATEGORIES.find((c) => c.id === state.category)?.name ?? "—"} onEdit={() => jumpTo(idx("category"))} />
                    <SummaryRow label="Modello" value={modelIsOther ? `Altro: ${state.model_other}` : optName(catalog.models, state.model_id) || "—"} onEdit={() => jumpTo(idx("model"))} />
                    <SummaryRow label="Tipo di design" value={designIsOther ? `Altro: ${state.design_other}` : optName(catalog.designs, state.design_id) || "—"} onEdit={() => jumpTo(idx("design"))} />
                    {!state.skip_details && <>
                      <SummaryRow label="Area" value={optName(catalog.areas, state.area_id) || "—"} onEdit={() => jumpTo(idx("area"))} />
                      <SummaryRow label="Tipo di realizzazione" value={optName(catalog.types, state.type_id) || "—"} onEdit={() => jumpTo(idx("realization"))} />
                      <SummaryRow label="Livello di dettaglio" value={optName(catalog.details, state.detail_id) || "—"} onEdit={() => jumpTo(idx("detail"))} />
                    </>}
                    {state.is_colors_only && <SummaryRow label="Modalità" value="Solo colori" />}
                    <SummaryRow label="Descrizione" value={state.description || "—"} onEdit={() => jumpTo(idx("description"))} />
                  </div>
                  <div className="max-w-2xl mt-6 border border-primary/40 bg-primary/5 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="summary-price">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Fascia di prezzo stimata</span>
                    <span className="font-display font-black text-xl sm:text-2xl whitespace-nowrap">{formatPrice(price)}</span>
                  </div>
                  <div className="flex gap-3 mt-8 max-w-2xl">
                    <button data-testid="step-prev" onClick={goPrev} className="px-5 py-4 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Indietro</button>
                    <button data-testid="summary-continue-button" onClick={goNext} className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-[0.15em] text-sm py-4 flex items-center justify-center gap-2 hover:-translate-y-[2px] transition-transform">Continua ai contatti <ArrowRight className="h-4 w-4" /></button>
                  </div>
                </>
              )}

              {currentKey === "contact" && (
                <>
                  <h1 className="font-display font-black text-4xl tracking-tighter mb-2">I tuoi contatti</h1>
                  <p className="text-muted-foreground mb-8">Lasciami i tuoi dati: ti ricontatterò per la tua richiesta.</p>
                  <div className="max-w-md space-y-5">
                    <Field label="Nome e cognome *" testid="contact-name" value={state.contact.full_name} onChange={(v) => patch({ contact: { ...state.contact, full_name: v } })} />
                    <Field label="Email *" testid="contact-email" value={state.contact.email} onChange={(v) => patch({ contact: { ...state.contact, email: v } })} />
                    <Field label="Telefono / WhatsApp (opzionale)" testid="contact-phone" value={state.contact.phone} onChange={(v) => patch({ contact: { ...state.contact, phone: v } })} />
                    <p className="text-xs text-muted-foreground -mt-2">L'<span className="text-foreground">email</span> è obbligatoria: ti invierò lì il riepilogo automatico della richiesta.</p>
                    <label data-testid="contact-privacy" className="flex items-start gap-3 border border-white/10 p-4 cursor-pointer hover:border-primary/50 transition-colors">
                      <input type="checkbox" checked={state.contact.privacy_accepted} onChange={(e) => patch({ contact: { ...state.contact, privacy_accepted: e.target.checked } })} className="mt-0.5 h-4 w-4 accent-primary" />
                      <span className="text-sm text-muted-foreground">Accetto l'informativa sulla <span className="text-foreground">privacy</span> e il trattamento dei miei dati. <span className="text-primary">*</span></span>
                    </label>
                  </div>
                  <div className="flex gap-3 mt-8 max-w-md">
                    <button data-testid="step-prev" onClick={goPrev} className="px-5 py-4 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Indietro</button>
                    <button data-testid="submit-request-button" onClick={submit} disabled={submitting || !contactValid(state.contact)} className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-[0.15em] text-sm py-4 flex items-center justify-center gap-2 hover:-translate-y-[2px] transition-transform disabled:opacity-50 disabled:hover:translate-y-0">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Invia richiesta <ArrowRight className="h-4 w-4" /></>}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

const BackOnly: React.FC<{ onPrev: () => void }> = ({ onPrev }) => (
  <div className="mt-8 max-w-2xl">
    <button data-testid="step-prev" onClick={onPrev} className="px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Indietro</button>
  </div>
);

const OtherField: React.FC<{ label: string; value: string; onChange: (v: string) => void; onNext: () => void; onPrev: () => void; testid: string }> = ({ label, value, onChange, onNext, onPrev, testid }) => (
  <div className="mt-8 max-w-md">
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-secondary block mb-2">{label} *</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} autoFocus
      className="w-full border border-input bg-black/50 py-3 px-4 text-sm outline-none focus:border-primary transition-colors" />
    <div className="flex gap-3 mt-5">
      <button data-testid="step-prev" onClick={onPrev} className="px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Indietro</button>
      <button data-testid="step-next" onClick={onNext} disabled={!value.trim()} className="flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-6 py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-50 disabled:hover:translate-y-0">Avanti <ArrowRight className="h-4 w-4" /></button>
    </div>
  </div>
);

const StepNav: React.FC<{ onPrev: () => void; onNext: () => void; nextDisabled?: boolean }> = ({ onPrev, onNext, nextDisabled }) => (
  <div className="flex gap-3 mt-10 max-w-2xl">
    <button data-testid="step-prev" onClick={onPrev} className="px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Indietro</button>
    <button data-testid="step-next" onClick={onNext} disabled={nextDisabled} className="flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-6 py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-50 disabled:hover:translate-y-0">Avanti <ArrowRight className="h-4 w-4" /></button>
  </div>
);

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; testid: string }> = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">{label}</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-input bg-black/50 py-3 px-4 text-sm outline-none focus:border-primary transition-colors" />
  </div>
);

const SummaryRow: React.FC<{ label: string; value: string; onEdit?: () => void }> = ({ label, value, onEdit }) => (
  <div className="flex items-center justify-between px-5 py-4 gap-4">
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</p>
      <p className="text-sm mt-0.5 truncate">{value}</p>
    </div>
    {onEdit && (
      <button onClick={onEdit} className="shrink-0 flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-secondary transition-colors"><Pencil className="h-3.5 w-3.5" /> Modifica</button>
    )}
  </div>
);
